import { getPillProps } from '../../../utils/documentStyles';
import { ChatPillData } from '../../../types/chatTypes';

/**
 * Crea un elemento DOM que representa una "pill" de mención para insertar en un contentEditable.
 * Encapsula el diseño visual y comportamiento básico.
 */
export const createMentionPill = (
  suggestion: any, 
  onRemove: () => void
): HTMLElement => {
  const pillSpan = document.createElement('span');
  pillSpan.contentEditable = 'false';
  
  // Obtener propiedades unificadas del motor de estilos
  const pillData: Partial<ChatPillData> = {
    id: suggestion.document?.id || suggestion.id,
    name: suggestion.name,
    type: suggestion.type,
    isCategory: suggestion.type === 'knowledge_base' || suggestion.type === 'folder',
    knowledgeBaseId: suggestion.knowledgeBaseId
  };

  const { styles, iconPath, label: displayLabel } = getPillProps(pillData);
  const closeIcon = `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  // Aplicar estilos base usando Tailwind-like classes o estilos inline para precisión
  pillSpan.className = `inline-flex items-center gap-1.5 px-2 py-0.5 mx-0.5 rounded-md font-medium text-[12px] align-baseline cursor-default select-none shadow-sm border transition-all`;
  pillSpan.style.backgroundColor = styles.hex.bg;
  pillSpan.style.borderColor = styles.hex.border;
  pillSpan.style.color = styles.hex.text;

  // Atributos de datos para lógica de negocio
  pillSpan.setAttribute('data-mention', 'true');
  pillSpan.setAttribute('data-document-id', pillData.id!);
  pillSpan.setAttribute('data-document-name', pillData.name!);
  pillSpan.setAttribute('data-type', pillData.type!);
  pillSpan.setAttribute('data-label', pillData.name!);

  // Estructura interna HTML
  const icon = document.createElement('img');
  icon.src = iconPath;
  icon.className = "w-3.5 h-3.5 object-contain opacity-80";
  icon.style.marginRight = '2px';

  const labelNode = document.createElement('span');
  labelNode.textContent = displayLabel;

  pillSpan.appendChild(icon);
  pillSpan.appendChild(labelNode);

  // Botón de cerrar
  const removeBtn = document.createElement('span');
  removeBtn.innerHTML = closeIcon;
  removeBtn.style.cssText = `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    margin-left: 4px;
    border-radius: 50%;
    cursor: pointer;
    opacity: 0.5;
    transition: all 0.2s ease;
  `;

  // Eventos de interacción para el botón
  removeBtn.onmouseenter = () => { 
    removeBtn.style.opacity = '1'; 
    removeBtn.style.backgroundColor = 'rgba(0,0,0,0.08)';
    removeBtn.style.color = '#ef4444'; 
  };
  removeBtn.onmouseleave = () => { 
    removeBtn.style.opacity = '0.5'; 
    removeBtn.style.backgroundColor = 'transparent';
    removeBtn.style.color = 'currentColor';
  };

  removeBtn.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    pillSpan.remove();
    onRemove();
  };

  pillSpan.appendChild(removeBtn);

  return pillSpan;
};

export interface ExecutionTemplatePillInput {
  id: string;
  name: string;
  templateType?: string | null;
}

/**
 * Pill for workpill_execution_templates row; serializes to web-style
 * `@[name](execution_template:id:word|excel)` in getProcessedText (handled in ChatInputArea).
 */
export const createExecutionTemplatePill = (
  et: ExecutionTemplatePillInput,
  onRemove: () => void
): HTMLElement => {
  const ext = et.templateType === 'excel' ? 'xlsx' : 'docx';
  const pillData: Partial<ChatPillData> = {
    id: et.id,
    name: et.name,
    type: 'template',
    isCategory: false,
  };
  const { styles, iconPath } = getPillProps({
    ...pillData,
    name: `${et.name}.${ext}`,
  });
  const closeIcon = `<svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

  const pillSpan = document.createElement('span');
  pillSpan.contentEditable = 'false';
  pillSpan.className = `inline-flex items-center gap-1.5 px-2 py-0.5 mx-0.5 rounded-md font-medium text-[12px] align-baseline cursor-default select-none shadow-sm border transition-all`;
  pillSpan.style.backgroundColor = styles.hex.bg;
  pillSpan.style.borderColor = styles.hex.border;
  pillSpan.style.color = styles.hex.text;

  pillSpan.setAttribute('data-mention', 'true');
  pillSpan.setAttribute('data-type', 'execution_template');
  pillSpan.setAttribute('data-execution-template-id', et.id);
  pillSpan.setAttribute('data-template-type', et.templateType || '');
  pillSpan.setAttribute('data-label', et.name);

  const icon = document.createElement('img');
  icon.src = iconPath;
  icon.className = 'w-3.5 h-3.5 object-contain opacity-80';
  icon.style.marginRight = '2px';

  const labelNode = document.createElement('span');
  labelNode.textContent = et.name;

  pillSpan.appendChild(icon);
  pillSpan.appendChild(labelNode);

  const removeBtn = document.createElement('span');
  removeBtn.innerHTML = closeIcon;
  removeBtn.style.cssText = `
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    margin-left: 4px;
    border-radius: 50%;
    cursor: pointer;
    opacity: 0.5;
    transition: all 0.2s ease;
  `;
  removeBtn.onmouseenter = () => {
    removeBtn.style.opacity = '1';
    removeBtn.style.backgroundColor = 'rgba(0,0,0,0.08)';
    removeBtn.style.color = '#ef4444';
  };
  removeBtn.onmouseleave = () => {
    removeBtn.style.opacity = '0.5';
    removeBtn.style.backgroundColor = 'transparent';
    removeBtn.style.color = 'currentColor';
  };
  removeBtn.onclick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    pillSpan.remove();
    onRemove();
  };
  pillSpan.appendChild(removeBtn);
  return pillSpan;
};

// Helper simple para escapar HTML y prevenir inyecciones básicas en el nombre
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
